// pages/overlay/Booth.jsx
// Menu-board overlay for booth visitors at the physical event.
// Loaded as an OBS browser source and projected to a booth TV via OBS
// Fullscreen Projector. The center "griddle" area is a transparent zone —
// OBS layers a Video Capture Device source (the griddle camera) behind the
// browser source to fill the hole.
//
// Landscape layout (legacy 16:9 rig):
//   ┌─ logo + queue stats (top bar) ────────────────────────────────┐
//   │ Flash       │  Griddle (transparent)  │  Rotating promo card  │
//   │ designs     │  decorative frame only  │  (boothShowcase)      │
//   │ grid        │                         │                       │
//   ├─────────────┴─────────────────────────┴───────────────────────┤
//   │ Drawing/Up Next · Cooking · Finished/Still Cooking (status)   │
//   └───────────────────────────────────────────────────────────────┘
//
// Portrait layout (1080×1920 — the rig default; vertical booth panel),
// restacked 2026-07-07 so the flash band shares the idle scenes' bottom slot
// (FlashBrandBand — the grid does not move across a Booth↔Break↔StartingSoon
// scene cut):
//   ┌─ logo (left) + queue stats (right) ─┐  ~108px
//   │ Status rail — Now Drawing/Up Next ·  │  11vh (locked under the top bar;
//   │ Now Cooking · Still/Just Finished    │   GalaRail here in gala mode)
//   │ Griddle (transparent, near-square    │  remainder ≈ 1080×862 at y≈319
//   │ 1080-wide window) — the ordering QR  │
//   │ (SummonBanner layer) lives IN here   │
//   │ Spotlight — wide card, no header:    │  14vh
//   │  text left · image right             │
//   │ Flash band — FlashBrandBand, same    │  24.5vh
//   │ slot as Break/StartingSoon           │
//   └──────────────────────────────────────┘
// NOTE: the status rail + spotlight + flash band heights feed the portrait
// alert position in AlertToast.jsx AND the camera crop numbers in
// docs/OBS-VERTICAL-RIG.md — keep all three in sync if this stack changes.
//
// No marquee on this scene (either orientation): the Gazette spotlight already
// rotates messaging, and two rotating text surfaces on one sign read as noise.
//
// Performance: this MAY get piped into OBS so animations are deliberately
// quiet — opacity-only crossfades, no box-shadow keyframes, no drop-shadow
// filter animations, one 1Hz interval for timers.

import { QRCodeSVG } from "qrcode.react";
import useOrientation from "./../../hooks/useOrientation";
import SummonBanner from "./../../components/overlay/SummonBanner";
import useOverlayPalette from "./../../hooks/useOverlayPalette";
import useFlashDesigns from "./../../hooks/useFlashDesigns";
import useCurrentShiftOrders from "./../../hooks/useCurrentShiftOrders";
import useStreamConfig from "./../../hooks/useStreamConfig";
import useBoothShowcase from "./../../hooks/useBoothShowcase";
import JoPLogo from "./../../components/overlay/JoPLogo";
import FlashDesignsPanel from "./../../components/overlay/FlashDesignsPanel";
import { FlashBrandBand } from "./../../components/overlay/IdleSceneKit";
import PromoPanel from "./../../components/overlay/PromoPanel";
import StatusRow, { statusRowShell, Divider } from "./../../components/overlay/StatusRail";
import { computeQueueWait, fmtWait } from "./../../utils/overlayFormat";

const GOLD = "var(--jop-gold)";
const CREAM = "var(--jop-cream)";
const PINK = "var(--jop-pink)";
const SKY = "var(--jop-highlight-blue)";
const GOLD_RGB = "var(--jop-gold-rgb)";
const D = "var(--jop-display)";

const COL_BG = "linear-gradient(180deg, var(--jop-grad-1) 0%, var(--jop-grad-2) 50%, var(--jop-grad-3) 100%)";
const BAR_BG = "linear-gradient(90deg, var(--jop-grad-1) 0%, var(--jop-grad-2) 60%, var(--jop-grad-3) 100%)";

// ── GRIDDLE ZONE — transparent center with decorative frame ──────────────────
// OBS composites the camera Video Capture Device source behind this region.
// The gradient border IS the frame for the entire center column — no nested
// title bar or inner box; the "🥞 LIVE" corner badge labels the zone.
function GriddleZone() {
  return (
    <div style={{
      flex: 1, width: "100%", minHeight: 0,
      position: "relative",
      overflow: "hidden",
      // CMY gradient border — same trick as Main.jsx WebcamFrame so the
      // griddle zone reads as the "show" panel. Box-shadow dropped to
      // reduce compositor work in OBS. Squared corners so the frame sits
      // flush against the adjacent gold column borders.
      border: "10px solid",
      borderImage: "linear-gradient(135deg, var(--jop-slot-drawing) 0%, var(--jop-slot-cooking) 50%, var(--jop-slot-done) 100%) 1",
      boxSizing: "border-box",
      background: "transparent",
    }}>
      <div style={{
        position: "absolute", top: "10px", left: "12px",
        background: "rgba(var(--jop-ink-rgb), 0.85)",
        border: `1px solid rgba(${GOLD_RGB}, 0.53)`,
        borderRadius: "8px", padding: "3px 12px",
        color: GOLD, fontSize: "0.95rem", fontWeight: 900,
        textTransform: "uppercase", letterSpacing: "0.1em",
        fontFamily: D,
      }}>
        🥞 LIVE
      </div>
    </div>
  );
}

// (The old OrderQrCard lived here — replaced 2026-07-07 by the SummonBanner
// scene-QR layer mounted inside the griddle feed, driven by resolveQrFlow()
// in utils/qrFlow.js. Legacy config/stream.showQrOnBooth + customQrUrl still
// work — the resolver reconstructs them for shifts without qrFlows.)

// ── GALA RAIL (bottom bar, gala mode) ────────────────────────────────────────
// Booking-pitch replacement for the live status row, for premade / grab-and-go
// events with no live queue (config/stream.boothGalaMode). A dead tracker reads
// as sloppy in front of a room full of prospective clients — this points the
// same strip at event bookings instead: catering pitch · get-a-quote QR · live
// art pitch. Fully static — no timers, no rotation, zero OBS compositor cost.
const QUOTE_URL = "https://thejoyofpancakes.com/quote";

function GalaPitchCell({ kicker, headline, body }) {
  return (
    <div style={{
      flex: 1, minWidth: 0, minHeight: 0,
      display: "flex", flexDirection: "column", justifyContent: "center",
      gap: "clamp(2px, 0.6vh, 10px)",
      padding: "0 clamp(1rem, 1.6vw, 2.2rem)",
      overflow: "hidden",
    }}>
      <div style={{
        color: GOLD, fontFamily: D, fontWeight: 900,
        fontSize: "clamp(13px, 1.1vw, 22px)",
        textTransform: "uppercase", letterSpacing: "0.12em", lineHeight: 1,
      }}>{kicker}</div>
      <div style={{
        color: "white", fontFamily: D, fontWeight: 900,
        fontSize: "clamp(22px, 2.1vw, 42px)", lineHeight: 1.1,
      }}>{headline}</div>
      <div style={{
        color: CREAM, opacity: 0.9, fontFamily: D, fontWeight: 600,
        fontSize: "clamp(15px, 1.3vw, 26px)", lineHeight: 1.25,
      }}>{body}</div>
    </div>
  );
}

function GalaRail({ portrait }) {
  return (
    <div style={statusRowShell(portrait)}>
      {/* Portrait rail is a slim ~11vh strip — only the quote cell fits;
          the side pitch cells would render as unreadable confetti. */}
      {!portrait && (
        <>
          <GalaPitchCell
            kicker="Also on the menu"
            headline="Gluten-Free Poffertjes"
            body="Mini Dutch pancakes by the dozen, plus full breakfast catering for gatherings of any size."
          />
          <Divider />
        </>
      )}
      {/* Center — get-a-quote QR. Rendered locally (qrcode.react) like the
          order QR; fg/bg stay literal for scannability, never themed. */}
      <div style={{
        flex: "1.2 1 0", minWidth: 0, minHeight: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: "clamp(0.8rem, 1.4vw, 1.8rem)",
        padding: "0 clamp(0.9rem, 1.4vw, 1.8rem)",
        overflow: "hidden",
      }}>
        <div style={{
          flexShrink: 0,
          background: "white",
          borderRadius: "12px",
          padding: "7px",
          width: portrait ? "clamp(90px, 8.5vh, 165px)" : "clamp(100px, 13.5vh, 210px)",
          height: portrait ? "clamp(90px, 8.5vh, 165px)" : "clamp(100px, 13.5vh, 210px)",
          boxSizing: "border-box",
          display: "flex",
        }}>
          <QRCodeSVG
            value={QUOTE_URL}
            size={256}
            bgColor="#ffffff"
            fgColor="#1d52a0"
            style={{ width: "100%", height: "100%" }}
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            color: GOLD, fontFamily: D, fontWeight: 900,
            fontSize: "clamp(36px, 2.3vw, 46px)", lineHeight: 1.1,
          }}>
            ✨ Get a Quote
          </div>
          <div style={{
            color: CREAM, opacity: 0.9, fontFamily: D, fontWeight: 600,
            fontSize: "clamp(22px, 1.3vw, 26px)", lineHeight: 1.25,
            marginTop: "clamp(2px, 0.6vh, 8px)",
          }}>
            Scan to bring us to your event. 2026 dates are going fast!
          </div>
        </div>
      </div>
      {!portrait && (
        <>
          <Divider />
          <GalaPitchCell
            kicker="A St. Louis original"
            headline="Live Pancake Art"
            body="An edible art show for weddings, galas & parties."
          />
        </>
      )}
    </div>
  );
}

// ── STATUS ROW ───────────────────────────────────────────────────────────────
// Extracted to components/overlay/StatusRail.jsx (2026-07-09) — the Main
// portrait info band mounts the same cells, so the two scenes share one
// design language and one set of text-fit rules. GalaRail above reuses the
// exported statusRowShell + Divider to stay pixel-identical to the live rail.

// ── TOP BAR ──────────────────────────────────────────────────────────────────
// Logo anchors the LEFT edge, wordmark hanging just below the bar's bottom
// edge (negative marginBottom) over the band beneath. Event name rides beside
// it; the right side is all queue tracking — depth + est. wait are the two
// numbers IRL guests actually want off this sign, so they get hero sizing and
// generous spacing. No marquee (the Gazette spotlight already rotates).
function TopBar({ activeShift, queueDepth, waitMin, portrait, galaMode }) {
  const barH = portrait ? 108 : 84;
  const logoH = Math.round(barH * 1.2);
  // Portrait: pin the pancake stack's flat cut edge onto the bar's bottom
  // gold border, so the pancakes read as sliced by the line while the
  // wordmark's script descenders drape over the status rail below (the
  // bar's zIndex sits above the rail's for exactly this). Measured from
  // /jop_stream.png (600×180): cut edge at 143/180 ≈ 0.797 of the image,
  // descenders at 176/180 ≈ 0.978 → offset = 0.203·logoH − 2 lands the cut
  // edge mid-border. Landscape keeps the original modest overhang.
  const logoOffset = portrait
    ? Math.round(logoH * 0.203) - 2
    : Math.round(barH * 0.24);
  const queueOpen = !!activeShift?.isQueueOpen;
  const statFs = portrait ? "3.3rem" : "2.4rem";
  const statLabelFs = portrait ? "1.3rem" : "0.9rem";
  return (
    <div style={{
      // Portrait: in-flow header of the flex-column stack. Landscape: absolute
      // bar over the absolutely-positioned middle area (legacy layout).
      ...(portrait
        ? { position: "relative", flexShrink: 0 }
        : { position: "absolute", top: 0, left: 0, right: 0 }),
      height: `${barH}px`,
      background: BAR_BG,
      borderBottom: `3px solid ${GOLD}`,
      boxShadow: "0 3px 12px rgba(var(--jop-gold-rgb), 0.22)",
      display: "flex", alignItems: "center",
      padding: "0 1.6rem 0 1.2rem",
      gap: "1.4rem",
      fontFamily: D,
      // Above the status rail (zIndex 3) so the logo's overhang — the
      // wordmark + cut-off pancakes hanging below the bar via negative
      // margin — lays OVER the top of the Up Next card. The PNG's
      // transparency sells the overlap; the rail must not paint over it.
      zIndex: 4,
      overflow: "visible",
      boxSizing: "border-box",
    }}>
      <JoPLogo
        height={logoH}
        style={{
          alignSelf: "flex-end",
          marginBottom: `-${logoOffset}px`,
          flexShrink: 0,
        }}
      />
      {activeShift?.eventName ? (
        // The shift title is the bar's one piece of identity copy — it gets
        // TWO lines and real weight before ellipsizing, instead of being
        // crushed to a sliver between the logo and the queue stats. It owns
        // the whole gap between logo and stats and centers in it, so it
        // reads as a placed title, not leftover text hugging the logo.
        <div style={{
          flex: 1, minWidth: 0, overflow: "hidden",
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          overflowWrap: "anywhere",
          textAlign: "center",
          color: CREAM, fontWeight: portrait ? 700 : 600,
          opacity: portrait ? 0.92 : 0.75,
          fontSize: portrait ? "1.5rem" : "1.15rem",
          lineHeight: 1.15,
          letterSpacing: "0.04em",
        }}>
          {activeShift.eventName}
        </div>
      ) : (
        <div style={{ flex: 1 }} />
      )}

      {/* Right — queue tracking, pinned to the right edge */}
      <div style={{
        flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "flex-end",
      }}>
        {queueOpen ? (
          <div style={{
            flexShrink: 0,
            display: "flex", alignItems: "center",
            // Portrait gap tightened 2.4→1.6rem — the reclaimed width goes to
            // the event name on the left, which was getting crushed.
            gap: portrait ? "1.6rem" : "1.8rem",
            paddingRight: "0.4rem",
          }}>
            <div style={{ textAlign: "center" }}>
              <div style={{
                color: "var(--jop-slot-cooking)", fontFamily: D, fontWeight: 900,
                fontSize: statFs, lineHeight: 1,
              }}>{queueDepth}</div>
              <div style={{
                color: "rgba(255,255,255,0.75)", fontFamily: D, fontWeight: 700,
                fontSize: statLabelFs,
                textTransform: "uppercase", letterSpacing: "0.08em",
                marginTop: "3px",
              }}>in queue</div>
            </div>
            <div style={{ width: "2px", height: portrait ? "64px" : "48px", background: "rgba(255,255,255,0.18)" }} />
            <div style={{ textAlign: "center" }}>
              <div style={{
                color: SKY, fontFamily: D, fontWeight: 900,
                fontSize: statFs, lineHeight: 1,
              }}>{fmtWait(waitMin)}</div>
              <div style={{
                color: "rgba(255,255,255,0.75)", fontFamily: D, fontWeight: 700,
                fontSize: statLabelFs,
                textTransform: "uppercase", letterSpacing: "0.08em",
                marginTop: "3px",
              }}>est. wait</div>
            </div>
          </div>
        ) : galaMode ? (
          // Gala mode: no live queue tonight by design — a pink "QUEUE CLOSED"
          // badge would undercut the booking pitch, so point at the site.
          <div style={{
            flexShrink: 0, textAlign: "center",
            color: GOLD, fontFamily: D, fontWeight: 900,
            fontSize: portrait ? "1.7rem" : "1.5rem",
            letterSpacing: "0.04em", lineHeight: 1.1,
          }}>
            ✨ thejoyofpancakes.com
            <div style={{
              color: "rgba(255,255,255,0.55)", fontWeight: 600,
              fontSize: portrait ? "1rem" : "0.9rem",
              letterSpacing: "0.04em", marginTop: "3px",
              textTransform: "uppercase",
            }}>Now booking</div>
          </div>
        ) : (
          <div style={{
            flexShrink: 0, textAlign: "center",
            color: PINK, fontFamily: D, fontWeight: 900,
            fontSize: portrait ? "2.2rem" : "1.7rem",
            letterSpacing: "0.06em", lineHeight: 1.1,
          }}>
            QUEUE CLOSED
            <div style={{
              color: "rgba(255,255,255,0.55)", fontWeight: 600,
              fontSize: portrait ? "1rem" : "0.9rem",
              letterSpacing: "0.04em", marginTop: "3px",
              textTransform: "uppercase",
            }}>{activeShift ? "Thanks for coming!" : "See us at the booth!"}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── MAIN ─────────────────────────────────────────────────────────────────────
export default function Booth() {
  useOverlayPalette();
  const orientation = useOrientation();
  const portrait = orientation === "portrait";
  const { activeShift, drawing, cookingAll, queued, allActive, recentlyDone, concurrentCount } = useCurrentShiftOrders();
  // Pass the active shift so the per-event flash board (activeFlashProfileIds)
  // renders on this overlay — without it, only the base IP set resolves.
  const { allDesigns, designMap } = useFlashDesigns(activeShift, { previewWhenIdle: true });
  const streamConfig = useStreamConfig();
  const { queue } = useBoothShowcase();

  // Gala mode — operator toggle (config/stream.boothGalaMode, Stream Setup →
  // Scenes). Swaps the live tracker rail for static booking-pitch cards at
  // premade/grab-and-go events. Falls back to the normal tracker when off.
  const galaMode = !!streamConfig.boothGalaMode;

  const waitMin = computeQueueWait(allActive, activeShift);
  const queueDepth = [drawing, ...(cookingAll || []), ...(queued || [])].filter(Boolean).length;
  const lastDone = recentlyDone[0] || null;

  // ── Portrait (1080×1920, the rig default) — one flex-column stack.
  // The status rail + spotlight band + flash band are all RIGID (fixed vh),
  // so the griddle window is the flex remainder: a stable, near-square
  // ~1080×862 transparent zone starting at y≈319 that the camera parks
  // behind (see docs/OBS-VERTICAL-RIG.md for the crop numbers). The flash
  // band is the shared FlashBrandBand (bottom 24.5vh) — identical slot on
  // Break/StartingSoon, so the flash grid never moves on a scene cut.
  if (portrait) {
    return (
      <div style={{
        width: "100vw", height: "100vh",
        background: "transparent",
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: "calc(18px + 0.6vw)",
        userSelect: "none", overflow: "hidden", position: "relative",
        color: "white",
        display: "flex", flexDirection: "column",
      }}>
        <TopBar
          activeShift={activeShift}
          queueDepth={queueDepth}
          waitMin={waitMin}
          portrait
          galaMode={galaMode}
        />

        {/* Status rail — what's coming up, locked under the top bar */}
        {galaMode ? (
          <GalaRail portrait />
        ) : (
          <StatusRow
            activeShift={activeShift}
            drawing={drawing}
            cookingAll={cookingAll}
            queued={queued}
            lastDone={lastDone}
            designMap={designMap}
            portrait
          />
        )}

        {/* Griddle — transparent flex remainder, near-square live window.
            The ordering QR (flow-resolved SummonBanner layer) lives on the
            feed — it can never cover the bands around it. */}
        <div style={{
          flex: 1, minHeight: 0,
          display: "flex", flexDirection: "column",
          overflow: "hidden",
          position: "relative",
        }}>
          <GriddleZone />
          <SummonBanner
            scene="booth"
            shift={activeShift}
          concurrentCount={concurrentCount}
            streamConfig={streamConfig}
            queueDepth={queueDepth}
            waitMin={waitMin}
            portrait
          />
        </div>

        {/* Gazette spotlight — wide band above the flash band */}
        <div style={{
          flexShrink: 0,
          height: "max(200px, 14vh)",
          background: COL_BG,
          borderTop: `3px solid ${GOLD}`,
          padding: "0.6rem 1rem 0.8rem",
          boxSizing: "border-box",
          display: "flex", flexDirection: "row", gap: "1rem",
          overflow: "hidden",
        }}>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <PromoPanel queue={queue} wide />
          </div>
        </div>

        {/* Flash band — shared bottom slot (spacer keeps the flex stack
            clear of the absolutely-positioned band). dust off: Booth runs
            all event long, keep the compositor quiet. No URL row here (the
            top bar carries the brand) — the band shaves exactly that row's
            52px so the flash GRID stays pixel-identical to Break/Starting
            Soon while the live feed above gets the height back. */}
        <div style={{ flexShrink: 0, height: "calc(24.5vh - 52px)" }} />
        <FlashBrandBand
          designs={allDesigns}
          active={false}
          dust={false}
          showLogo={false}
          showUrl={false}
          spotlight={galaMode}
        />
      </div>
    );
  }

  // ── Landscape (legacy 16:9 rig) — absolute top/bottom bars + 3-column middle.
  const TOP_H = 84;
  // Must mirror statusRowShell(false).height so the middle area stops exactly
  // at the top of the viewport-scaled status row.
  const BOT_H = "max(134px, 17vh)";

  return (
    <div style={{
      width: "100vw", height: "100vh",
      background: "transparent",
      fontFamily: "system-ui, -apple-system, sans-serif",
      fontSize: "calc(18px + 0.6vw)",
      userSelect: "none", overflow: "hidden", position: "relative",
      color: "white",
    }}>
      <TopBar
        activeShift={activeShift}
        queueDepth={queueDepth}
        waitMin={waitMin}
        portrait={false}
        galaMode={galaMode}
      />

      {/* Middle area — 3 columns */}
      <div style={{
        position: "absolute",
        top: TOP_H, bottom: BOT_H, left: 0, right: 0,
        display: "flex",
        flexDirection: "row",
        gap: 0,
      }}>
        {/* Left — flash designs */}
        <div style={{
          flex: "0 0 21%",
          minWidth: 0, minHeight: 0,
          background: COL_BG,
          borderRight: `3px solid ${GOLD}`,
          boxShadow: "inset -8px 0 20px rgba(var(--jop-gold-rgb), 0.08)",
          padding: "1rem 1.1rem",
          boxSizing: "border-box",
          display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}>
          <FlashDesignsPanel designs={allDesigns} portrait={false} spotlight={galaMode} />
        </div>

        {/* Center — griddle transparent zone. No padding: gradient frame
            sits flush against the adjacent column gold borders. The ordering
            QR (flow-resolved SummonBanner layer) anchors to this feed. */}
        <div style={{
          flex: 1, minWidth: 0, minHeight: 0,
          boxSizing: "border-box",
          display: "flex", flexDirection: "column",
          overflow: "hidden",
          position: "relative",
        }}>
          <GriddleZone />
          <SummonBanner
            scene="booth"
            shift={activeShift}
          concurrentCount={concurrentCount}
            streamConfig={streamConfig}
            queueDepth={queueDepth}
            waitMin={waitMin}
            portrait={false}
          />
        </div>

        {/* Right — promo rotation */}
        <div style={{
          flex: "0 0 21%",
          minWidth: 0, minHeight: 0,
          background: COL_BG,
          borderLeft: `3px solid ${GOLD}`,
          boxShadow: "inset 8px 0 20px rgba(var(--jop-gold-rgb), 0.08)",
          padding: "1rem 1.1rem",
          boxSizing: "border-box",
          display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}>
          <PromoPanel queue={queue} />
        </div>
      </div>

      {galaMode ? (
        <GalaRail portrait={false} />
      ) : (
        <StatusRow
          activeShift={activeShift}
          drawing={drawing}
          cookingAll={cookingAll}
          queued={queued}
          lastDone={lastDone}
          designMap={designMap}
          portrait={false}
        />
      )}

    </div>
  );
}
